import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const levelList = [
  { name: '游客',     threshold: 0,          direct: '—',   color: 'bg-gray-50 text-gray-300',      bar: 'bg-gray-200' },
  { name: '普通用户', threshold: 0,          direct: '—',   color: 'bg-gray-100 text-gray-400',     bar: 'bg-gray-300' },
  { name: '会员',     threshold: 300,        direct: '20%', color: 'bg-gray-100 text-gray-500',     bar: 'bg-gray-400' },
  { name: '销售员',   threshold: 9000,       direct: '25%', color: 'bg-emerald-50 text-emerald-600', bar: 'bg-emerald-400' },
  { name: '批发',     threshold: 45000,      direct: '30%', color: 'bg-sky-50 text-sky-600',         bar: 'bg-sky-400' },
  { name: '主管',     threshold: 270000,     direct: '35%', color: 'bg-amber-50 text-amber-600',     bar: 'bg-amber-400' },
  { name: '经理',     threshold: 1350000,    direct: '40%', color: 'bg-orange-50 text-orange-600',   bar: 'bg-orange-400' },
  { name: '总监',     threshold: 4500000,    direct: '45%', color: 'bg-rose-50 text-rose-600',       bar: 'bg-rose-400' },
  { name: '董事',     threshold: 32400000,   direct: '50%', color: 'bg-violet-50 text-violet-600',   bar: 'bg-violet-400' },
  { name: '总裁',     threshold: 90000000,   direct: '55%', color: 'bg-red-50 text-red-600',         bar: 'bg-red-500' },
];

const getLevelMeta = (name: string) =>
  levelList.find((l) => l.name === name) ?? levelList[0];

const getNextLevel = (name: string) => {
  const idx = levelList.findIndex((l) => l.name === name);
  return idx >= 0 && idx < levelList.length - 1 ? levelList[idx + 1] : null;
};

const getLevelProgress = (performance: number, levelName: string) => {
  const current = levelList.find((l) => l.name === levelName);
  const next = getNextLevel(levelName);
  if (!next || !current) return 100;
  const range = next.threshold - current.threshold;
  if (range <= 0) return 0;
  return Math.min(Math.max(Math.round(((performance - current.threshold) / range) * 100), 0), 99);
};

const levelDotColor: Record<string, string> = {
  '总裁': 'bg-red-500',
  '董事': 'bg-violet-500',
  '总监': 'bg-rose-400',
  '经理': 'bg-orange-400',
  '主管': 'bg-amber-400',
  '批发': 'bg-sky-400',
  '销售员': 'bg-emerald-400',
  '会员': 'bg-gray-300',
  '普通用户': 'bg-gray-200',
};

interface TeamMember {
  id: string;
  nickname: string | null;
  phone: string | null;
  avatar_url: string | null;
  level: string;
  performance: number;
  created_at: string;
  // 本月业绩从 orders 聚合
  monthPerformance: number;
  // 下级人数
  teamSize: number;
  // 层级深度（1=直推，2+=间推）
  depth: number;
  // 上级 id
  referrer_id: string | null;
}

type SortKey = 'performance' | 'monthPerformance' | 'teamSize';

const CommunityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sortKey, setSortKey] = useState<SortKey>('performance');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [myProfile, setMyProfile] = useState<{
    nickname: string;
    avatar_url: string | null;
    level: string;
    performance: number;
  } | null>(null);

  const [members, setMembers] = useState<TeamMember[]>([]);

  // 当日团队业绩
  interface TodayLog {
    id: string;
    user_id: string;
    delta: number;
    remark: string | null;
    created_at: string;
  }
  const [todayLogs, setTodayLogs] = useState<TodayLog[]>([]);
  const [todayLoading, setTodayLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    // 1. 拉取自己的最新数据
    const { data: me } = await supabase
      .from('custom_users')
      .select('nickname, avatar_url, level, performance')
      .eq('id', user.id)
      .maybeSingle();

    if (me) {
      setMyProfile({
        nickname: me.nickname ?? '我',
        avatar_url: me.avatar_url,
        level: me.level ?? '普通用户',
        performance: Number(me.performance ?? 0),
      });
    }

    // 2. 用递归函数拉取所有层级下级（带 depth）
    const { data: allSubs } = await supabase.rpc('get_all_subordinates', { root_id: user.id });

    if (!allSubs || allSubs.length === 0) {
      setMembers([]);
      setLoading(false);
      return;
    }

    const allIds = allSubs.map((s: { id: string }) => s.id);

    // 3. 批量拉取所有下级的用户信息
    const { data: usersData } = await supabase
      .from('custom_users')
      .select('id, nickname, phone, avatar_url, level, performance, created_at, referrer_id')
      .in('id', allIds);

    if (!usersData || usersData.length === 0) {
      setMembers([]);
      setLoading(false);
      return;
    }

    // 4. 本月业绩 + 累计真实消费（从 orders 表聚合，避免 performance 字段含上级同步数据）
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const [{ data: monthOrders }, { data: allOrders }] = await Promise.all([
      supabase
        .from('orders')
        .select('user_id, total_amount')
        .in('user_id', allIds)
        .neq('status', '已退款')
        .gte('created_at', monthStart),
      supabase
        .from('orders')
        .select('user_id, total_amount')
        .in('user_id', allIds)
        .neq('status', '已退款'),
    ]);

    const monthMap: Record<string, number> = {};
    (monthOrders ?? []).forEach((o) => {
      monthMap[o.user_id] = (monthMap[o.user_id] ?? 0) + Number(o.total_amount ?? 0);
    });

    // 真实个人累计消费（从 orders 聚合，不含上级同步的 performance）
    const realSpentMap: Record<string, number> = {};
    (allOrders ?? []).forEach((o) => {
      realSpentMap[o.user_id] = (realSpentMap[o.user_id] ?? 0) + Number(o.total_amount ?? 0);
    });

    // 5. 每个成员名下的下级人数（只算直接下级的递归，即 get_all_subordinates 里 depth > 当前 depth 且 referrer 链路经过该成员）
    // 用 allSubs 里的 depth 信息构建 depth map
    const depthMap: Record<string, number> = {};
    allSubs.forEach((s: { id: string; depth: number }) => {
      depthMap[s.id] = s.depth;
    });

    // 统计每个成员名下的下级人数：allSubs 中 referrer_id 链路经过该成员的数量
    // 简化：对每个成员调用 get_all_subordinates
    const subMap: Record<string, number> = {};
    await Promise.all(
      allIds.map(async (mid: string) => {
        const { data: subs } = await supabase.rpc('get_all_subordinates', { root_id: mid });
        subMap[mid] = (subs ?? []).length;
      })
    );

    // 6. 组合数据（performance 用 orders 真实消费额，避免上级业绩同步污染）
    const result: TeamMember[] = usersData.map((m) => ({
      id: m.id,
      nickname: m.nickname,
      phone: m.phone,
      avatar_url: m.avatar_url,
      level: m.level ?? '普通用户',
      performance: realSpentMap[m.id] ?? 0,
      created_at: m.created_at,
      monthPerformance: monthMap[m.id] ?? 0,
      teamSize: subMap[m.id] ?? 0,
      depth: depthMap[m.id] ?? 1,
      referrer_id: m.referrer_id,
    }));

    // 按 depth 升序，同 depth 按 performance 降序
    result.sort((a, b) => {
      if (a.depth !== b.depth) return a.depth - b.depth;
      return b.performance - a.performance;
    });

    setMembers(result);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 拉取当日团队业绩记录（所有直指下级今天的 performance 记录）
  const fetchTodayLogs = useCallback(async () => {
    if (!user?.id) return;
    setTodayLoading(true);
    // 构造北京时间（UTC+8）今天 0 点对应的 UTC 时间
    // 北京时间 = UTC + 8，所以北京时间今天 0 点 = UTC 今天 0 点 - 8 小时
    // 即 UTC 昨天 16:00
    const now = new Date();
    // 用 UTC 时间计算北京时间的今天日期
    const cstNow = new Date(now.getTime() + 8 * 60 * 60 * 1000);
    const cstYear = cstNow.getUTCFullYear();
    const cstMonth = cstNow.getUTCMonth();
    const cstDate = cstNow.getUTCDate();
    // 北京时间今天 0 点 = UTC 时间
    const utcMidnight = new Date(Date.UTC(cstYear, cstMonth, cstDate, 0, 0, 0) - 8 * 60 * 60 * 1000);

    // 拿所有层级下级 id（无限层）
    const { data: allSubs } = await supabase.rpc('get_all_subordinates', { root_id: user.id });

    const ids = (allSubs ?? []).map((d: { id: string }) => d.id);
    if (ids.length === 0) {
      setTodayLogs([]);
      setTodayLoading(false);
      return;
    }

    // 查当日所有相关记录：order（消费，delta负）+ performance（业绩，delta正）
    const { data } = await supabase
      .from('points_log')
      .select('id, user_id, delta, remark, created_at, source')
      .in('user_id', ids)
      .in('source', ['order', 'performance'])
      .gte('created_at', utcMidnight.toISOString())
      .order('created_at', { ascending: false });

    const rawLogs = data ?? [];
    // 去重：同一笔订单有 order + performance 两条，优先用 performance（正数）
    // 旧数据只有 order 记录时，转为正数补充
    const perfOrderNos = new Set<string>();
    rawLogs.forEach((l) => {
      if (l.source === 'performance') {
        const m = l.remark?.match(/FZY\d+/);
        if (m) perfOrderNos.add(m[0]);
      }
    });
    const deduped = rawLogs.reduce<typeof rawLogs>((acc, l) => {
      if (l.source === 'performance') {
        acc.push(l);
      } else if (l.source === 'order') {
        const m = l.remark?.match(/FZY\d+/);
        const no = m?.[0];
        if (!no || !perfOrderNos.has(no)) {
          acc.push({ ...l, delta: Math.abs(l.delta) });
        }
      }
      return acc;
    }, []);
    setTodayLogs(deduped);
    setTodayLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchTodayLogs(); }, [fetchTodayLogs]);

  // 定时轮询，每 30 秒刷新一次当日业绩
  useEffect(() => {
    const timer = setInterval(() => { fetchTodayLogs(); }, 30000);
    return () => clearInterval(timer);
  }, [fetchTodayLogs]);

  const sorted = [...members].sort((a, b) => {
    if (a.depth !== b.depth) return a.depth - b.depth;
    return b[sortKey] - a[sortKey];
  });

  // 团队总人数 = 所有层级下级（不含自己）
  const totalTeam = members.length;
  const directMembers = members.filter((m) => m.depth === 1);
  // 累计团队消费 = 所有层级成员的真实消费总和（不只是直推）
  const directPerformance = members.reduce((sum, m) => sum + m.performance, 0);
  const monthTotal = members.reduce((sum, m) => sum + m.monthPerformance, 0);

  const myLevel = getLevelMeta(myProfile?.level ?? '普通用户');
  const myNext = getNextLevel(myLevel.name);
  const myPerformance = myProfile?.performance ?? 0;

  const sortTabs: { key: SortKey; label: string }[] = [
    { key: 'performance', label: '累计业绩' },
    { key: 'monthPerformance', label: '本月业绩' },
    { key: 'teamSize', label: '团队人数' },
  ];

  // 默认头像
  const avatarChar = (name: string | null) => (name ?? '?')[0].toUpperCase();
  const avatarColors = [
    'from-rose-400 to-pink-500', 'from-orange-400 to-amber-500',
    'from-emerald-400 to-teal-500', 'from-violet-400 to-purple-500',
    'from-sky-400 to-cyan-500', 'from-red-400 to-rose-500',
  ];
  const avatarGradient = (name: string | null) => {
    const idx = (name ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % avatarColors.length;
    return avatarColors[idx];
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#f7f7f9] flex flex-col items-center justify-center max-w-[480px] mx-auto px-8">
        <i className="ri-team-line text-4xl text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm mb-4">请先登录查看团队</p>
        <button onClick={() => navigate('/login')} className="bg-rose-500 text-white px-8 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
          去登录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f9] pb-24 max-w-[480px] mx-auto">

      {/* Header */}
      <div className="bg-gradient-to-br from-rose-500 via-rose-400 to-orange-300 pt-12 pb-16 px-5 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute bottom-0 -left-6 w-28 h-28 rounded-full bg-white/10" />

        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/80 text-sm mb-5 cursor-pointer">
          <i className="ri-arrow-left-s-line text-lg" /> 返回
        </button>

        <div className="flex items-center gap-4 relative z-10">
          {/* 头像 */}
          <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-white/50 flex-shrink-0">
            {myProfile?.avatar_url ? (
              <img src={myProfile.avatar_url} alt="avatar" className="w-full h-full object-cover object-top" />
            ) : (
              <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarGradient(myProfile?.nickname ?? null)}`}>
                <span className="text-white font-bold text-xl">{avatarChar(myProfile?.nickname ?? null)}</span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-white font-bold text-lg">{myProfile?.nickname ?? '加载中...'}</p>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-white/25 text-white">
                {myLevel.name}
              </span>
            </div>
            <p className="text-white/70 text-xs">
              直推返佣 {myLevel.direct}
              {myLevel.name !== '普通用户' && ' · 级差收益 10%'}
            </p>
            {myNext && (
              <div className="mt-2">
                <span className="text-white/60 text-xs">
                  升级至 {myNext.name} 还差 {Math.max(0, myNext.threshold - myPerformance).toLocaleString()} 积分
                  <span className="ml-1 opacity-70">（已消费 {myPerformance.toLocaleString()} 积分）</span>
                </span>
                <div className="h-1 bg-white/20 rounded-full overflow-hidden w-40 mt-1">
                  <div
                    className="h-full bg-white/80 rounded-full transition-all duration-1000"
                    style={{ width: `${getLevelProgress(myPerformance, myLevel.name)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats overview */}
      <div className="mx-4 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="grid grid-cols-3 divide-x divide-gray-50">
            <div className="flex flex-col items-center py-4">
              {loading ? (
                <div className="w-8 h-5 bg-gray-100 rounded animate-pulse mb-0.5" />
              ) : (
                <p className="text-gray-900 font-bold text-xl">{directMembers.length}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">直推</p>
            </div>
            <div className="flex flex-col items-center py-4">
              {loading ? (
                <div className="w-8 h-5 bg-gray-100 rounded animate-pulse mb-0.5" />
              ) : (
                <p className="text-gray-900 font-bold text-xl">{totalTeam}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">团队总人数</p>
            </div>
            <div className="flex flex-col items-center py-4">
              {loading ? (
                <div className="w-12 h-5 bg-rose-50 rounded animate-pulse mb-0.5" />
              ) : (
                <p className="text-rose-500 font-bold text-xl">{monthTotal.toLocaleString()}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">本月业绩</p>
            </div>
          </div>
          <div className="border-t border-gray-50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <i className="ri-bar-chart-line text-rose-400 text-sm" />
              <span className="text-xs text-gray-500">累计团队消费</span>
            </div>
            <span className="text-rose-500 font-bold text-sm">{directPerformance.toLocaleString()} 积分</span>
          </div>
        </div>
      </div>

      {/* Invite button */}
      <div className="mx-4 mt-3">
        <button
          onClick={() => navigate('/invite')}
          className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line text-base" />
          邀请好友加入团队
        </button>
      </div>

      {/* 当日团队业绩卡片 */}
      <div className="bg-white mt-3 mx-4 rounded-2xl overflow-hidden mb-3">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-calendar-check-line text-rose-400 text-base" />
            </div>
            <span className="text-sm font-bold text-gray-900">当日团队业绩</span>
            <span className="text-xs text-gray-400">{new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })}</span>
          </div>
          <button
            onClick={() => fetchTodayLogs()}
            className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer whitespace-nowrap"
            title="刷新"
          >
            <i className={`ri-refresh-line text-base ${todayLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* 今日合计 */}
        <div className="px-4 py-3 flex items-center justify-between bg-rose-50/40">
          <div className="flex items-center gap-1.5">
            <i className="ri-team-line text-rose-300 text-sm" />
            <span className="text-xs text-gray-500">团队今日合计</span>
          </div>
          <span className="text-rose-500 font-bold text-base">
            {todayLoading ? (
              <span className="inline-block w-12 h-4 bg-rose-100 rounded animate-pulse" />
            ) : (
              `${todayLogs.reduce((s, l) => s + l.delta, 0).toLocaleString()} 积分`
            )}
          </span>
        </div>

        {/* 按成员聚合展示 */}
        {todayLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-5 h-5 border-2 border-rose-300 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : todayLogs.length === 0 ? (
          <div className="flex flex-col items-center py-6">
            <i className="ri-inbox-line text-3xl text-gray-200 mb-1.5" />
            <p className="text-xs text-gray-400">今日暂无团队业绩记录</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {/* 按成员聚合 */}
            {(() => {
              // 聚合每个成员的今日业绩
              const memberMap: Record<string, { total: number; logs: typeof todayLogs }> = {};
              todayLogs.forEach((log) => {
                if (!memberMap[log.user_id]) memberMap[log.user_id] = { total: 0, logs: [] };
                memberMap[log.user_id].total += log.delta;
                memberMap[log.user_id].logs.push(log);
              });
              return Object.entries(memberMap)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([uid, info]) => {
                  const member = members.find((m) => m.id === uid);
                  const name = member?.nickname ?? '团队成员';
                  const gradientIdx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % avatarColors.length;
                  return (
                    <div key={uid} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0">
                        {member?.avatar_url ? (
                          <img src={member.avatar_url} alt={name} className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarColors[gradientIdx]}`}>
                            <span className="text-white font-bold text-sm">{name[0].toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {info.logs.length} 条记录 · 最近 {new Date(info.logs[0].created_at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <span className="text-rose-500 font-bold text-sm whitespace-nowrap">+{info.total.toLocaleString()} 积分</span>
                    </div>
                  );
                });
            })()}
          </div>
        )}
      </div>

      {/* Member list */}
      <div className="bg-white mt-3 mx-4 rounded-2xl overflow-hidden mb-4">
        {/* Sort tabs */}
        <div className="flex items-center border-b border-gray-50 px-4 pt-4 pb-3 gap-1">
          <span className="text-sm font-bold text-gray-900 mr-2 whitespace-nowrap">全部成员</span>
          <div className="flex gap-1 ml-auto">
            {sortTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSortKey(tab.key)}
                className={`text-xs px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  sortKey === tab.key ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <i className="ri-team-line text-5xl mb-3 text-gray-200" />
            <p className="text-sm font-medium text-gray-500 mb-1">暂无团队成员</p>
            <p className="text-xs text-gray-400 mb-5">邀请好友加入，一起赚取分销收益</p>
            <button
              onClick={() => navigate('/invite')}
              className="bg-rose-500 text-white text-sm px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap"
            >
              立即邀请
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {sorted.map((member, index) => {
              const lv = getLevelMeta(member.level);
              const nextLv = getNextLevel(member.level);
              const progress = getLevelProgress(member.performance, member.level);
              const isExpanded = expandedId === member.id;
              const dotColor = levelDotColor[member.level] ?? 'bg-gray-200';
              const isDirect = member.depth === 1;
              // 缩进：每层 16px，最多缩进 4 层
              const indentPx = Math.min(member.depth - 1, 4) * 16;

              return (
                <div key={member.id}>
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer active:bg-gray-50 transition-colors text-left"
                    style={{ paddingLeft: `${16 + indentPx}px` }}
                    onClick={() => setExpandedId(isExpanded ? null : member.id)}
                  >
                    {/* 层级标识线 */}
                    {!isDirect && (
                      <div className="flex-shrink-0 flex items-center" style={{ width: `${indentPx}px`, marginLeft: `-${indentPx}px` }}>
                        <div className="w-3 h-px bg-gray-200 ml-auto" />
                      </div>
                    )}

                    {/* 直推/间推 badge */}
                    <div className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      isDirect
                        ? 'bg-rose-50 text-rose-500'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isDirect ? '直推' : `${member.depth}级`}
                    </div>

                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100">
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt={member.nickname ?? ''} className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarGradient(member.nickname)}`}>
                            <span className="text-white font-bold text-sm">{avatarChar(member.nickname)}</span>
                          </div>
                        )}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center ${dotColor}`}>
                        <i className="ri-vip-crown-fill text-white text-[8px]" />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{member.nickname ?? '未知用户'}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${lv.color}`}>
                          {member.level}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-team-line text-gray-300 text-xs" />
                        <span className="text-xs text-gray-400">团队 {member.teamSize} 人</span>
                      </div>
                    </div>

                    {/* Performance + arrow */}
                    <div className="text-right flex-shrink-0 flex items-center gap-1">
                      <div>
                        <p className="text-rose-500 font-bold text-sm">
                          {sortKey === 'teamSize'
                            ? `${member.teamSize} 人`
                            : `${(member[sortKey] as number).toLocaleString()} 积分`}
                        </p>
                        <p className="text-xs text-gray-400">
                          {sortKey === 'performance' ? '累计消费' : sortKey === 'monthPerformance' ? '本月消费' : '团队人数'}
                        </p>
                      </div>
                      <i className={`ri-arrow-down-s-line text-gray-300 text-lg transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50/60" style={{ paddingLeft: `${16 + indentPx}px` }}>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-white rounded-xl p-2.5 text-center">
                          <p className="text-gray-800 font-bold text-sm">{member.performance.toLocaleString()}</p>
                          <p className="text-xs text-gray-400 mt-0.5">累计消费积分</p>
                        </div>
                        <div className="bg-white rounded-xl p-2.5 text-center">
                          <p className="text-rose-500 font-bold text-sm">{member.monthPerformance.toLocaleString()}</p>
                          <p className="text-xs text-gray-400 mt-0.5">本月消费积分</p>
                        </div>
                        <div className="bg-white rounded-xl p-2.5 text-center">
                          <p className="text-emerald-500 font-bold text-sm">{lv.direct}</p>
                          <p className="text-xs text-gray-400 mt-0.5">直推返佣</p>
                        </div>
                      </div>

                      {/* Level progress */}
                      <div className="bg-white rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-gray-500">
                            {nextLv
                              ? `距升级 ${nextLv.name} 还差 ${Math.max(0, nextLv.threshold - member.performance).toLocaleString()} 积分`
                              : '已达最高等级'}
                          </span>
                          <span className="text-xs text-gray-400">{progress}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${lv.bar} transition-all duration-700`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                          加入时间：{new Date(member.created_at).toLocaleDateString('zh-CN')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

export default CommunityPage;
